import React, { useState, useEffect, useRef } from 'react';
import mermaid from 'mermaid';
import { 
  FiBook, 
  FiUsers, 
  FiFileText, 
  FiShield, 
  FiSettings, 
  FiDownload, 
  FiMail, 
  FiDatabase,
  FiCloud,
  FiChevronRight,
  FiChevronDown,
  FiInfo,
  FiCheckCircle,
  FiAlertTriangle,
  FiUser,
  FiBriefcase,
  FiEye
} from 'react-icons/fi';

// Mermaid component for rendering diagrams
const MermaidDiagram: React.FC<{ chart: string; id: string }> = ({ chart, id }) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      mermaid.initialize({
        startOnLoad: true,
        theme: 'default',
        // 'strict' (default) escapes HTML in node labels and disables click
        // handlers — safer if any chart input ever becomes user-controlled.
        securityLevel: 'strict',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 14,
        flowchart: {
          useMaxWidth: true,
          htmlLabels: true,
          curve: 'basis'
        }
      });
      
      mermaid.render(id, chart).then(({ svg }) => {
        if (ref.current) {
          ref.current.innerHTML = svg;
        }
      });
    }
  }, [chart, id]);

  return <div ref={ref} className="flex justify-center" />;
};

const DocumentationPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['overview']));

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const scrollToSection = (section: string) => {
    setActiveSection(section);
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const sections = [
    { id: 'overview', title: 'System Overview', icon: FiBook },
    { id: 'architecture', title: 'Architecture', icon: FiCloud },
    { id: 'user-roles', title: 'User Roles & Permissions', icon: FiUsers },
    { id: 'workflows', title: 'Application Workflows', icon: FiFileText },
    { id: 'security', title: 'Security & Compliance', icon: FiShield },
    { id: 'troubleshooting', title: 'Troubleshooting', icon: FiInfo },
  ];

  const StatusBadge: React.FC<{ status: string; description: string }> = ({ status, description }) => {
    const getStatusColor = (status: string) => {
      const colors: Record<string, string> = {
        'draft': 'bg-gray-100 text-gray-700 border-gray-300',
        'new_submission': 'bg-blue-100 text-blue-700 border-blue-300',
        'under_review': 'bg-yellow-100 text-yellow-700 border-yellow-300',
        'requested_rework': 'bg-orange-100 text-orange-700 border-orange-300',
        'profile_verified': 'bg-green-100 text-green-700 border-green-300',
        'available_for_hire': 'bg-purple-100 text-purple-700 border-purple-300',
        'hired': 'bg-emerald-100 text-emerald-700 border-emerald-300',
        'rejected': 'bg-red-100 text-red-700 border-red-300',
        'terminated': 'bg-gray-100 text-gray-700 border-gray-300',
        'blacklisted': 'bg-red-200 text-red-800 border-red-400',
      };
      return colors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
    };

    return (
      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(status)}`}>
        <span className="capitalize">{status.replace('_', ' ')}</span>
        <div className="ml-2 group relative">
          <FiInfo className="h-3 w-3 cursor-help" />
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
            {description}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-primary-900 mb-4 flex items-center">
          <FiBook className="mr-4 h-8 w-8 text-brand-600" />
          System Documentation
        </h1>
        <p className="text-lg text-primary-600 max-w-3xl">
          Comprehensive guide to the Trigon Employee Onboarding & Management System. 
          Learn how to effectively use all features and understand the complete workflow.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Table of Contents */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-soft border border-primary-200/50 p-6 sticky top-6">
            <h2 className="text-lg font-semibold text-primary-900 mb-4">Contents</h2>
            <nav className="space-y-2">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`w-full text-left flex items-center px-3 py-2 rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-brand-50 text-brand-700 border-l-4 border-brand-500'
                        : 'text-primary-600 hover:text-brand-600 hover:bg-primary-50'
                    }`}
                  >
                    <Icon className="mr-3 h-4 w-4 flex-shrink-0" />
                    <span className="text-sm font-medium">{section.title}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-8">
          
          {/* System Overview */}
          <section id="overview" className="bg-white rounded-xl shadow-soft border border-primary-200/50 p-8">
            <div className="flex items-center mb-6">
              <FiBook className="mr-4 h-6 w-6 text-brand-600" />
              <h2 className="text-2xl font-bold text-primary-900">System Overview</h2>
            </div>
            
            <div className="prose prose-primary max-w-none">
              <p className="text-lg text-primary-700 mb-6">
                The Trigon Employee Onboarding & Management System is a comprehensive, cloud-based platform 
                designed to streamline the entire employee lifecycle from application to hiring and beyond.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-br from-brand-50 to-brand-100 p-6 rounded-xl">
                  <FiUsers className="h-8 w-8 text-brand-600 mb-3" />
                  <h3 className="text-lg font-semibold text-primary-900 mb-2">Multi-Role System</h3>
                  <p className="text-primary-600">Support for Candidates, HR Clerks, Managers, and Administrators with role-based access control.</p>
                </div>
                <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-6 rounded-xl">
                  <FiCloud className="h-8 w-8 text-primary-600 mb-3" />
                  <h3 className="text-lg font-semibold text-primary-900 mb-2">Cloud-Native</h3>
                  <p className="text-primary-600">Built on AWS with serverless architecture for scalability, reliability, and cost-effectiveness.</p>
                </div>
                <div className="bg-gradient-to-br from-success-50 to-success-100 p-6 rounded-xl">
                  <FiShield className="h-8 w-8 text-success-600 mb-3" />
                  <h3 className="text-lg font-semibold text-primary-900 mb-2">Secure & Compliant</h3>
                  <p className="text-primary-600">Enterprise-grade security with comprehensive audit trails and data protection.</p>
                </div>
              </div>

              <div className="bg-primary-50 border-l-4 border-primary-500 p-6 rounded-r-lg">
                <div className="flex items-start">
                  <FiInfo className="h-5 w-5 text-primary-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-primary-900 font-semibold mb-2">Key Benefits</h4>
                    <ul className="text-primary-700 space-y-1">
                      <li>• Streamlined application process with dynamic forms</li>
                      <li>• Automated workflow management and notifications</li>
                      <li>• Comprehensive document management and verification</li>
                      <li>• Real-time collaboration between HR teams and managers</li>
                      <li>• Complete audit trail for compliance and reporting</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Architecture */}
          <section id="architecture" className="bg-white rounded-xl shadow-soft border border-primary-200/50 p-8">
            <div className="flex items-center mb-6">
              <FiCloud className="mr-4 h-6 w-6 text-brand-600" />
              <h2 className="text-2xl font-bold text-primary-900">System Architecture</h2>
            </div>

            <div className="mb-8">
              {/* Architecture Diagram */}
              <div className="bg-gray-50 p-6 rounded-xl mb-8">
                <h3 className="text-lg font-semibold text-primary-900 mb-4 text-center">System Architecture Overview</h3>
                <div className="bg-white p-4 rounded-lg shadow-sm overflow-x-auto">
                  <MermaidDiagram 
                    id="architecture-diagram"
                    chart={`graph LR
    subgraph "Frontend"
        A[React App]
        B[Authentication]
        C[File Upload]
        D[Forms]
    end
    
    subgraph "AWS Infrastructure"
        E[CloudFront CDN]
        F[API Gateway]
        G[AWS Cognito]
        H[Lambda Functions]
        I[DynamoDB]
        J[S3 Storage]
        K[Mailgun]
    end
    
    A --> E
    E --> F
    F --> H
    B --> G
    C --> J
    H --> I
    H --> K
    H --> J
    
    subgraph "Lambda Services"
        L[Auth Service]
        M[Employee Service]
        N[File Service]
        O[Notification Service]
        P[Audit Service]
        Q[Export Service]
    end
    
    H --> L
    H --> M
    H --> N
    H --> O
    H --> P
    H --> Q
    
    classDef frontend fill:#e3f2fd
    classDef aws fill:#fff3e0
    classDef lambda fill:#e8f5e8
    
    class A,B,C,D frontend
    class E,F,G,I,J,K aws
    class L,M,N,O,P,Q lambda`}
                  />
                </div>
              </div>

              <div className="bg-gradient-to-r from-primary-50 to-brand-50 p-6 rounded-xl mb-6">
                <h3 className="text-lg font-semibold text-primary-900 mb-4">Technology Stack</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-primary-800 mb-3">Frontend</h4>
                    <ul className="space-y-2 text-primary-600">
                      <li className="flex items-center"><FiCheckCircle className="h-4 w-4 text-success-500 mr-2" /> React 18 + TypeScript</li>
                      <li className="flex items-center"><FiCheckCircle className="h-4 w-4 text-success-500 mr-2" /> Tailwind CSS</li>
                      <li className="flex items-center"><FiCheckCircle className="h-4 w-4 text-success-500 mr-2" /> React Hook Form</li>
                      <li className="flex items-center"><FiCheckCircle className="h-4 w-4 text-success-500 mr-2" /> React Router</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-primary-800 mb-3">Backend & Infrastructure</h4>
                    <ul className="space-y-2 text-primary-600">
                      <li className="flex items-center"><FiCheckCircle className="h-4 w-4 text-success-500 mr-2" /> AWS Lambda (Node.js)</li>
                      <li className="flex items-center"><FiCheckCircle className="h-4 w-4 text-success-500 mr-2" /> Amazon DynamoDB</li>
                      <li className="flex items-center"><FiCheckCircle className="h-4 w-4 text-success-500 mr-2" /> AWS Cognito</li>
                      <li className="flex items-center"><FiCheckCircle className="h-4 w-4 text-success-500 mr-2" /> Amazon S3</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold text-primary-900 mb-4">System Components</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-primary-200">
                    <FiDatabase className="h-6 w-6 text-primary-600 mb-2" />
                    <h4 className="font-semibold text-primary-900">Data Layer</h4>
                    <p className="text-sm text-primary-600">DynamoDB for employee records, audit logs, and system configuration</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-primary-200">
                    <FiShield className="h-6 w-6 text-primary-600 mb-2" />
                    <h4 className="font-semibold text-primary-900">Authentication</h4>
                    <p className="text-sm text-primary-600">AWS Cognito with role-based access control and JWT tokens</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-primary-200">
                    <FiMail className="h-6 w-6 text-primary-600 mb-2" />
                    <h4 className="font-semibold text-primary-900">Notifications</h4>
                    <p className="text-sm text-primary-600">Mailgun integration for automated email communications</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* User Roles & Permissions */}
          <section id="user-roles" className="bg-white rounded-xl shadow-soft border border-primary-200/50 p-8">
            <div className="flex items-center mb-6">
              <FiUsers className="mr-4 h-6 w-6 text-brand-600" />
              <h2 className="text-2xl font-bold text-primary-900">User Roles & Permissions</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Candidate Role */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <FiUser className="h-6 w-6 text-blue-600 mr-3" />
                  <h3 className="text-xl font-semibold text-primary-900">Candidate</h3>
                </div>
                <p className="text-primary-600 mb-4">Job applicants who submit their information and documents for review.</p>
                <h4 className="font-semibold text-primary-800 mb-2">Capabilities:</h4>
                <ul className="space-y-1 text-primary-600">
                  <li>• Submit job applications</li>
                  <li>• Upload required documents</li>
                  <li>• Save drafts and resume later</li>
                  <li>• Track application status</li>
                  <li>• Respond to rework requests</li>
                  <li>• View application history</li>
                </ul>
              </div>

              {/* HR Clerk Role */}
              <div className="bg-gradient-to-br from-brand-50 to-brand-100 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <FiBriefcase className="h-6 w-6 text-brand-600 mr-3" />
                  <h3 className="text-xl font-semibold text-primary-900">HR Clerk</h3>
                </div>
                <p className="text-primary-600 mb-4">HR professionals who review applications and manage the verification process.</p>
                <h4 className="font-semibold text-primary-800 mb-2">Capabilities:</h4>
                <ul className="space-y-1 text-primary-600">
                  <li>• Review new submissions</li>
                  <li>• Verify documents and information</li>
                  <li>• Request rework from candidates</li>
                  <li>• Approve or reject applications</li>
                  <li>• Move candidates to hiring pool</li>
                  <li>• Send email notifications</li>
                </ul>
              </div>

              {/* Manager Role */}
              <div className="bg-gradient-to-br from-success-50 to-success-100 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <FiEye className="h-6 w-6 text-success-600 mr-3" />
                  <h3 className="text-xl font-semibold text-primary-900">Manager</h3>
                </div>
                <p className="text-primary-600 mb-4">Hiring managers who review verified candidates and make hiring decisions.</p>
                <h4 className="font-semibold text-primary-800 mb-2">Capabilities:</h4>
                <ul className="space-y-1 text-primary-600">
                  <li>• View available candidates</li>
                  <li>• Review candidate profiles</li>
                  <li>• Access complete audit history</li>
                  <li>• Make hiring decisions</li>
                  <li>• Export candidate data</li>
                  <li>• View documentation</li>
                </ul>
              </div>

              {/* Admin Role */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <FiSettings className="h-6 w-6 text-purple-600 mr-3" />
                  <h3 className="text-xl font-semibold text-primary-900">Administrator</h3>
                </div>
                <p className="text-primary-600 mb-4">System administrators with full access to all features and system management.</p>
                <h4 className="font-semibold text-primary-800 mb-2">Capabilities:</h4>
                <ul className="space-y-1 text-primary-600">
                  <li>• All HR Clerk capabilities</li>
                  <li>• User management and creation</li>
                  <li>• System configuration</li>
                  <li>• Full audit trail access</li>
                  <li>• Data export and reporting</li>
                  <li>• System maintenance</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Application Workflows */}
          <section id="workflows" className="bg-white rounded-xl shadow-soft border border-primary-200/50 p-8">
            <div className="flex items-center mb-6">
              <FiFileText className="mr-4 h-6 w-6 text-brand-600" />
              <h2 className="text-2xl font-bold text-primary-900">Application Workflows</h2>
            </div>

            <div className="mb-8">
              <h3 className="text-xl font-semibold text-primary-900 mb-4">Application Status Flow</h3>
              <p className="text-primary-600 mb-6">
                Every application moves through a structured workflow with specific statuses. Here's how the process works:
              </p>

              {/* Visual Workflow Diagram */}
              <div className="bg-white p-6 rounded-xl shadow-soft border border-primary-200/50 mb-8">
                <h3 className="text-2xl font-bold text-primary-900 mb-6 text-center">Application Workflow Diagram</h3>
                <p className="text-center text-primary-600 mb-6">
                  This diagram shows the complete journey from application to hiring decision, with 📧 email notifications
                  clearly marked at each trigger point, including the complete rework loop process.
                </p>
                <div className="bg-gray-50 p-6 rounded-lg overflow-x-auto">
                  <MermaidDiagram
                    id="workflow-diagram"
                    chart={`flowchart TD
    Start([Candidate Begins Application]) --> Draft[Draft Status<br/>Save Progress & Resume Later]
    Draft --> Submit[Submit Complete Application<br/>Reference Number Generated]
    Submit -->|📧 Submission Received Email<br/>with Reference Number| NewSub[New Submission<br/>Awaiting HR Review]

    NewSub --> HRReview{HR Clerk Review<br/>Document Verification}

    HRReview -->|Issues Found| Rework[Request Rework<br/>Flag Specific Fields]
    Rework -->|📧 Rework Required Email<br/>with Issues List & Secure Link| CandUpdate[Candidate Updates<br/>Via Secure Portal]
    CandUpdate -->|📧 Update Notification<br/>to HR Team| HRReview

    HRReview -->|All Good| Verified[Profile Verified<br/>Ready for Hiring Pool]
    Verified -->|📧 Internal Notification<br/>to Management Team| Available[Available for Hire<br/>Visible to Managers]

    HRReview -->|Major Issues| Rejected1[Rejected<br/>Does Not Meet Requirements]
    Rejected1 -->|📧 Rejection Email<br/>with Reason & Feedback| RejectedEnd1[Application Closed]

    Available --> ManagerReview{Manager Review<br/>Hiring Decision}

    ManagerReview -->|Selected| Hired[Hired<br/>Employee Onboarding Begins]
    Hired -->|📧 Welcome Email<br/>with Credentials & Details| Current[Current Employee<br/>Active Employment]

    ManagerReview -->|Not Selected| Rejected2[Not Selected<br/>Remains in Pool for Other Positions]
    Rejected2 -->|📧 Optional Feedback<br/>to Candidate| PoolReturn[Back to Candidate Pool]

    Current --> Terminated[Terminated<br/>Employment Ended]

    Rejected1 --> Blacklisted[Blacklisted<br/>Serious Issues/Fraud]
    Terminated --> Blacklisted

    %% Email trigger styling
    classDef emailTrigger fill:#e3f2fd,stroke:#1976d2,stroke-width:2px,color:#000,stroke-dasharray: 5 5
    classDef startEnd fill:#e3f2fd,stroke:#1976d2,stroke-width:2px,color:#000
    classDef process fill:#f3e5f5,stroke:#7b1fa2,stroke-width:1px,color:#000
    classDef decision fill:#fff3e0,stroke:#f57c00,stroke-width:2px,color:#000
    classDef success fill:#e8f5e8,stroke:#388e3c,stroke-width:1px,color:#000
    classDef warning fill:#fff8e1,stroke:#fbc02d,stroke-width:1px,color:#000
    classDef error fill:#ffebee,stroke:#d32f2f,stroke-width:1px,color:#000

    class Start,Current startEnd
    class Draft,Submit,NewSub,Verified,Available,Hired,CandUpdate process
    class HRReview,ManagerReview decision
    class Rework warning
    class Rejected1,Rejected2,Terminated,Blacklisted,RejectedEnd1 error
    class PoolReturn success`}
                  />
                </div>
              </div>

              {/* Email Triggers Legend */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
                <h3 className="text-lg font-semibold text-primary-900 mb-4 flex items-center">
                  <FiMail className="mr-2 h-5 w-5 text-blue-600" />
                  Email Notifications in the Workflow
                </h3>
                <p className="text-primary-600 mb-4">
                  The 📧 symbols in the diagram above show exactly when emails are automatically triggered. Here's what happens:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2">📧 To Candidates</h4>
                    <ul className="text-sm text-primary-600 space-y-1">
                      <li>• <strong>Submission Received:</strong> Confirmation with reference number</li>
                      <li>• <strong>Rework Required:</strong> Issues list with secure update link</li>
                      <li>• <strong>Welcome Email:</strong> Employee credentials and onboarding info</li>
                      <li>• <strong>Rejection Notice:</strong> Reason and feedback (if applicable)</li>
                    </ul>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2">📧 To HR/Management</h4>
                    <ul className="text-sm text-primary-600 space-y-1">
                      <li>• <strong>Update Notification:</strong> When candidate submits changes</li>
                      <li>• <strong>Internal Alerts:</strong> Status changes requiring attention</li>
                      <li>• <strong>Verification Complete:</strong> New candidates ready for hiring</li>
                      <li>• <strong>System Notifications:</strong> Process confirmations</li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Key Point:</strong> The rework loop clearly shows that when a candidate makes updates,
                    the application goes back to HR review with an email notification, ensuring no updates are missed.
                  </p>
                </div>
              </div>

              {/* Comprehensive Workflow Section */}
              <div className="bg-gradient-to-br from-primary-50 to-brand-50 p-8 rounded-xl mb-8">
                <h3 className="text-2xl font-bold text-primary-900 mb-6 text-center">Complete Application Workflow</h3>
                
                {/* Workflow Phases */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white p-4 rounded-lg border-l-4 border-blue-500 shadow-sm">
                    <h4 className="font-bold text-blue-700 mb-2">Phase 1: Application</h4>
                    <p className="text-sm text-primary-600">Candidate submits initial application with documents</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border-l-4 border-yellow-500 shadow-sm">
                    <h4 className="font-bold text-yellow-700 mb-2">Phase 2: HR Review</h4>
                    <p className="text-sm text-primary-600">HR verifies documents and information accuracy</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border-l-4 border-purple-500 shadow-sm">
                    <h4 className="font-bold text-purple-700 mb-2">Phase 3: Candidate Pool</h4>
                    <p className="text-sm text-primary-600">Verified candidates available for hiring consideration</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border-l-4 border-green-500 shadow-sm">
                    <h4 className="font-bold text-green-700 mb-2">Phase 4: Hiring Decision</h4>
                    <p className="text-sm text-primary-600">Managers review and make final hiring decisions</p>
                  </div>
                </div>

                {/* Detailed Step-by-Step Process */}
                <div className="bg-white p-6 rounded-xl shadow-sm">
                  <h4 className="text-xl font-semibold text-primary-900 mb-6 text-center">Detailed Process Flow</h4>
                  
                  <div className="space-y-6">
                    {/* Step 1: Candidate Application */}
                    <div className="flex items-start space-x-4 p-4 bg-blue-50 rounded-lg">
                      <div className="flex-shrink-0 w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg">1</div>
                      <div className="flex-1">
                        <h5 className="text-lg font-semibold text-primary-900 mb-2">Candidate Starts Application</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <StatusBadge status="draft" description="Application in progress, can be saved and resumed" />
                            <p className="text-primary-600 mt-2 text-sm">
                              • Candidate fills personal information<br/>
                              • Uploads required documents (ID, certificates, CV)<br/>
                              • Can save progress and continue later<br/>
                              • Form validation ensures completeness
                            </p>
                          </div>
                          <div>
                            <p className="text-primary-600 text-sm">
                              <strong>Required Information:</strong><br/>
                              • Personal details (name, ID, contact)<br/>
                              • Employment history and status<br/>
                              • Banking details for payroll<br/>
                              • Dependent information<br/>
                              • Position applied for
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 2: Submission */}
                    <div className="flex items-start space-x-4 p-4 bg-indigo-50 rounded-lg">
                      <div className="flex-shrink-0 w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-lg">2</div>
                      <div className="flex-1">
                        <h5 className="text-lg font-semibold text-primary-900 mb-2">Application Submitted</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <StatusBadge status="new_submission" description="Fresh application awaiting HR review" />
                            <p className="text-primary-600 mt-2 text-sm">
                              • System generates unique reference number<br/>
                              • Confirmation email sent to candidate<br/>
                              • Application appears in HR queue<br/>
                              • Automatic audit log entry created
                            </p>
                          </div>
                          <div>
                            <div className="bg-white p-3 rounded border">
                              <p className="text-sm text-primary-600">
                                <strong>Email Sent:</strong> "Application Received"<br/>
                                <strong>Contains:</strong> Reference number, next steps, timeline
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 3: HR Review */}
                    <div className="flex items-start space-x-4 p-4 bg-yellow-50 rounded-lg">
                      <div className="flex-shrink-0 w-12 h-12 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-lg">3</div>
                      <div className="flex-1">
                        <h5 className="text-lg font-semibold text-primary-900 mb-2">HR Clerk Review Process</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <StatusBadge status="under_review" description="HR is actively reviewing the application" />
                            <p className="text-primary-600 mt-2 text-sm">
                              • HR Clerk opens application for review<br/>
                              • Verifies all uploaded documents<br/>
                              • Checks information completeness<br/>
                              • Uses verification checklist
                            </p>
                          </div>
                          <div>
                            <div className="bg-white p-3 rounded border">
                              <p className="text-sm text-primary-600">
                                <strong>Verification Checklist:</strong><br/>
                                ✓ Personal details accuracy<br/>
                                ✓ Contact information validity<br/>
                                ✓ Document authenticity<br/>
                                ✓ Employment history verification<br/>
                                ✓ Dependent information (if applicable)
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 4: HR Decision Branch */}
                    <div className="flex items-start space-x-4 p-4 bg-orange-50 rounded-lg">
                      <div className="flex-shrink-0 w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg">4</div>
                      <div className="flex-1">
                        <h5 className="text-lg font-semibold text-primary-900 mb-2">HR Makes Decision</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {/* Rework Path */}
                          <div className="bg-white p-4 rounded border border-orange-200">
                            <StatusBadge status="requested_rework" description="Issues found, candidate needs to update" />
                            <p className="text-primary-600 mt-2 text-sm">
                              <strong>If Issues Found:</strong><br/>
                              • HR flags specific fields<br/>
                              • Detailed feedback provided<br/>
                              • Email with secure resubmission link<br/>
                              • Candidate can update and resubmit
                            </p>
                          </div>
                          {/* Approval Path */}
                          <div className="bg-white p-4 rounded border border-green-200">
                            <StatusBadge status="profile_verified" description="Application verified and approved" />
                            <p className="text-primary-600 mt-2 text-sm">
                              <strong>If Approved:</strong><br/>
                              • All information verified<br/>
                              • Documents authenticated<br/>
                              • Ready for candidate pool<br/>
                              • Confirmation email sent
                            </p>
                          </div>
                          {/* Rejection Path */}
                          <div className="bg-white p-4 rounded border border-red-200">
                            <StatusBadge status="rejected" description="Application rejected" />
                            <p className="text-primary-600 mt-2 text-sm">
                              <strong>If Rejected:</strong><br/>
                              • Does not meet requirements<br/>
                              • Rejection reason documented<br/>
                              • Notification email sent<br/>
                              • Process ends
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 5: Candidate Pool */}
                    <div className="flex items-start space-x-4 p-4 bg-purple-50 rounded-lg">
                      <div className="flex-shrink-0 w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">5</div>
                      <div className="flex-1">
                        <h5 className="text-lg font-semibold text-primary-900 mb-2">Added to Candidate Pool</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <StatusBadge status="available_for_hire" description="Candidate ready for hiring consideration" />
                            <p className="text-primary-600 mt-2 text-sm">
                              • HR moves verified candidate to hiring pool<br/>
                              • Profile becomes visible to managers<br/>
                              • Searchable and filterable by criteria<br/>
                              • Complete audit history available
                            </p>
                          </div>
                          <div>
                            <div className="bg-white p-3 rounded border">
                              <p className="text-sm text-primary-600">
                                <strong>Manager Access:</strong><br/>
                                • View complete candidate profile<br/>
                                • Review all uploaded documents<br/>
                                • See HR verification notes<br/>
                                • Access full audit trail
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 6: Manager Decision */}
                    <div className="flex items-start space-x-4 p-4 bg-green-50 rounded-lg">
                      <div className="flex-shrink-0 w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg">6</div>
                      <div className="flex-1">
                        <h5 className="text-lg font-semibold text-primary-900 mb-2">Manager Hiring Decision</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <div className="space-y-3">
                              <div>
                                <StatusBadge status="hired" description="Candidate selected for employment" />
                                <p className="text-primary-600 mt-1 text-sm">
                                  • Manager selects candidate for position<br/>
                                  • Employee details can be updated<br/>
                                  • Welcome email sent automatically<br/>
                                  • Onboarding process begins
                                </p>
                              </div>
                            </div>
                          </div>
                          <div>
                            <div>
                              <StatusBadge status="rejected" description="Not selected for this position" />
                              <p className="text-primary-600 mt-1 text-sm">
                                • Manager decides not to hire<br/>
                                • Candidate remains in pool for other positions<br/>
                                • Decision documented with reason<br/>
                                • Optional feedback to candidate
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Step 7: Post-Hire */}
                    <div className="flex items-start space-x-4 p-4 bg-emerald-50 rounded-lg">
                      <div className="flex-shrink-0 w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg">7</div>
                      <div className="flex-1">
                        <h5 className="text-lg font-semibold text-primary-900 mb-2">Employee Lifecycle Management</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className="bg-white p-4 rounded border border-emerald-200">
                            <StatusBadge status="hired" description="Recently hired employee" />
                            <p className="text-primary-600 mt-2 text-sm">
                              <strong>Initial Status:</strong><br/>
                              • Employee number assigned<br/>
                              • Job details updated<br/>
                              • Department assignment<br/>
                              • Onboarding materials sent
                            </p>
                          </div>
                          <div className="bg-white p-4 rounded border border-gray-200">
                            <StatusBadge status="terminated" description="Employment ended" />
                            <p className="text-primary-600 mt-2 text-sm">
                              <strong>If Employment Ends:</strong><br/>
                              • Termination reason documented<br/>
                              • Final processing completed<br/>
                              • Access revoked<br/>
                              • Audit trail preserved
                            </p>
                          </div>
                          <div className="bg-white p-4 rounded border border-red-200">
                            <StatusBadge status="blacklisted" description="Banned from future applications" />
                            <p className="text-primary-600 mt-2 text-sm">
                              <strong>If Serious Issues:</strong><br/>
                              • Fraud or misconduct<br/>
                              • Permanent ban from system<br/>
                              • Reason documented<br/>
                              • Cannot reapply
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-semibold text-sm">1</div>
                  <div className="flex-1">
                    <StatusBadge status="draft" description="Application in progress, not yet submitted" />
                    <p className="text-primary-600 mt-2">Candidate is filling out the application form. Can save progress and continue later.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-sm">2</div>
                  <div className="flex-1">
                    <StatusBadge status="new_submission" description="Fresh application awaiting initial review" />
                    <p className="text-primary-600 mt-2">Application submitted and awaiting HR review. Candidate receives confirmation email with reference number.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600 font-semibold text-sm">3</div>
                  <div className="flex-1">
                    <StatusBadge status="under_review" description="HR is currently reviewing the application" />
                    <p className="text-primary-600 mt-2">HR Clerk is actively reviewing documents and information for completeness and accuracy.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 font-semibold text-sm">4a</div>
                  <div className="flex-1">
                    <StatusBadge status="requested_rework" description="Issues found, candidate needs to update application" />
                    <p className="text-primary-600 mt-2">HR found issues that need correction. Candidate receives detailed feedback and secure resubmission link.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 font-semibold text-sm">4b</div>
                  <div className="flex-1">
                    <StatusBadge status="profile_verified" description="Application verified and approved by HR" />
                    <p className="text-primary-600 mt-2">All information and documents verified. Ready to be added to candidate pool for hiring consideration.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 font-semibold text-sm">5</div>
                  <div className="flex-1">
                    <StatusBadge status="available_for_hire" description="Candidate is ready for hiring consideration" />
                    <p className="text-primary-600 mt-2">Candidate added to hiring pool. Visible to managers for review and hiring decisions.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-semibold text-sm">6</div>
                  <div className="flex-1">
                    <StatusBadge status="hired" description="Candidate has been hired and is now an employee" />
                    <p className="text-primary-600 mt-2">Manager has decided to hire the candidate. Employee details can be updated with job-specific information.</p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
                <div className="flex items-start">
                  <FiAlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-yellow-900 font-semibold mb-2">Alternative Outcomes</h4>
                    <div className="space-y-2">
                      <div><StatusBadge status="rejected" description="Application rejected at any stage" /> - Application does not meet requirements</div>
                      <div><StatusBadge status="terminated" description="Employee contract terminated" /> - For hired employees only</div>
                      <div><StatusBadge status="blacklisted" description="Candidate banned from future applications" /> - Serious violations or fraud</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Candidate Journey */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <FiUser className="h-6 w-6 text-blue-600 mr-3" />
                  <h3 className="text-lg font-semibold text-primary-900">Candidate Journey</h3>
                </div>
                <ol className="space-y-3 text-primary-600">
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">1</span>
                    <span className="text-sm">Complete application form with personal details and employment history</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">2</span>
                    <span className="text-sm">Upload required documents (ID, certificates, CV, qualifications)</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">3</span>
                    <span className="text-sm">Submit application and receive unique reference number via email</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">4</span>
                    <span className="text-sm">Track application status and respond to any rework requests</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">5</span>
                    <span className="text-sm">Update information if requested and resubmit via secure link</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">6</span>
                    <span className="text-sm">Await final hiring decision and receive outcome notification</span>
                  </li>
                </ol>
              </div>

              {/* HR Clerk Journey */}
              <div className="bg-gradient-to-br from-brand-50 to-brand-100 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <FiBriefcase className="h-6 w-6 text-brand-600 mr-3" />
                  <h3 className="text-lg font-semibold text-primary-900">HR Clerk Journey</h3>
                </div>
                <ol className="space-y-3 text-primary-600">
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">1</span>
                    <span className="text-sm">Review new submissions in the queue and prioritize by submission date</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">2</span>
                    <span className="text-sm">Verify all uploaded documents for authenticity and completeness</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">3</span>
                    <span className="text-sm">Cross-check personal information and employment history accuracy</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">4</span>
                    <span className="text-sm">Flag issues for rework or approve application for candidate pool</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">5</span>
                    <span className="text-sm">Send detailed feedback emails and manage resubmission process</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-brand-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">6</span>
                    <span className="text-sm">Move verified candidates to "Available for Hire" status</span>
                  </li>
                </ol>
              </div>

              {/* Manager Journey */}
              <div className="bg-gradient-to-br from-success-50 to-success-100 p-6 rounded-xl">
                <div className="flex items-center mb-4">
                  <FiEye className="h-6 w-6 text-success-600 mr-3" />
                  <h3 className="text-lg font-semibold text-primary-900">Manager Journey</h3>
                </div>
                <ol className="space-y-3 text-primary-600">
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-success-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">1</span>
                    <span className="text-sm">Access pool of HR-verified candidates available for hiring</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-success-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">2</span>
                    <span className="text-sm">Filter and search candidates by position, qualifications, and experience</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-success-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">3</span>
                    <span className="text-sm">Review complete candidate profiles including documents and HR notes</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-success-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">4</span>
                    <span className="text-sm">Access full audit trail to understand candidate's application journey</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-success-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">5</span>
                    <span className="text-sm">Make hiring decisions and document reasons for selection/rejection</span>
                  </li>
                  <li className="flex items-start">
                    <span className="flex-shrink-0 w-6 h-6 bg-success-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-3 mt-0.5">6</span>
                    <span className="text-sm">Update employee records with job details and initiate onboarding</span>
                  </li>
                </ol>
              </div>
            </div>
          </section>

          {/* Key Role Differences */}
          <section className="bg-white rounded-xl shadow-soft border border-primary-200/50 p-8">
            <h3 className="text-2xl font-bold text-primary-900 mb-6 text-center">Key Differences: HR Clerk vs Manager</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-brand-50 p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-brand-700 mb-4 flex items-center">
                  <FiBriefcase className="mr-2" /> HR Clerk Focus
                </h4>
                <ul className="space-y-2 text-primary-600">
                  <li>• <strong>Document Verification:</strong> Ensures all documents are authentic and complete</li>
                  <li>• <strong>Data Accuracy:</strong> Verifies personal information and employment history</li>
                  <li>• <strong>Compliance Checking:</strong> Ensures application meets all regulatory requirements</li>
                  <li>• <strong>Quality Control:</strong> Acts as gatekeeper before candidates reach managers</li>
                  <li>• <strong>Process Management:</strong> Handles rework requests and candidate communication</li>
                  <li>• <strong>Administrative Tasks:</strong> Manages application status and system workflows</li>
                </ul>
              </div>
              <div className="bg-success-50 p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-success-700 mb-4 flex items-center">
                  <FiEye className="mr-2" /> Manager Focus
                </h4>
                <ul className="space-y-2 text-primary-600">
                  <li>• <strong>Strategic Selection:</strong> Evaluates candidates based on role requirements</li>
                  <li>• <strong>Skills Assessment:</strong> Reviews qualifications and experience relevance</li>
                  <li>• <strong>Team Fit Evaluation:</strong> Considers cultural and team compatibility</li>
                  <li>• <strong>Business Needs:</strong> Aligns hiring decisions with department goals</li>
                  <li>• <strong>Final Decision Making:</strong> Has authority to hire or reject candidates</li>
                  <li>• <strong>Onboarding Planning:</strong> Initiates employee setup and job assignments</li>
                </ul>
              </div>
            </div>
            <div className="mt-6 p-4 bg-primary-50 rounded-lg">
              <p className="text-primary-700 text-center">
                <strong>Key Point:</strong> HR Clerks focus on <em>verification and compliance</em>, while Managers focus on <em>strategic selection and business fit</em>. 
                Both roles are essential but serve different purposes in the hiring pipeline.
              </p>
            </div>
          </section>

          {/* Key Features */}
          <section id="features" className="bg-white rounded-xl shadow-soft border border-primary-200/50 p-8">
            <div className="flex items-center mb-6">
              <FiSettings className="mr-4 h-6 w-6 text-brand-600" />
              <h2 className="text-2xl font-bold text-primary-900">Key Features</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl">
                <FiFileText className="h-8 w-8 text-blue-600 mb-4" />
                <h3 className="text-lg font-semibold text-primary-900 mb-3">Dynamic Forms</h3>
                <p className="text-primary-600 mb-3">Comprehensive application forms with validation, file uploads, and progress saving.</p>
                <ul className="text-sm text-primary-600 space-y-1">
                  <li>• Personal information collection</li>
                  <li>• Document upload with type validation</li>
                  <li>• Draft saving and resumption</li>
                  <li>• Real-time validation</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl">
                <FiShield className="h-8 w-8 text-green-600 mb-4" />
                <h3 className="text-lg font-semibold text-primary-900 mb-3">Security & Audit</h3>
                <p className="text-primary-600 mb-3">Enterprise-grade security with comprehensive audit trails and access controls.</p>
                <ul className="text-sm text-primary-600 space-y-1">
                  <li>• Role-based access control</li>
                  <li>• Field-level change tracking</li>
                  <li>• Complete audit history</li>
                  <li>• Secure file storage</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl">
                <FiMail className="h-8 w-8 text-purple-600 mb-4" />
                <h3 className="text-lg font-semibold text-primary-900 mb-3">Email Notifications</h3>
                <p className="text-primary-600 mb-3">Automated email system with customizable templates for all workflow stages.</p>
                <ul className="text-sm text-primary-600 space-y-1">
                  <li>• Application confirmation</li>
                  <li>• Rework request notifications</li>
                  <li>• Status update alerts</li>
                  <li>• Welcome emails</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-xl">
                <FiDownload className="h-8 w-8 text-orange-600 mb-4" />
                <h3 className="text-lg font-semibold text-primary-900 mb-3">Data Export</h3>
                <p className="text-primary-600 mb-3">Flexible data export capabilities for reporting and analysis.</p>
                <ul className="text-sm text-primary-600 space-y-1">
                  <li>• CSV and Excel formats</li>
                  <li>• Filtered exports</li>
                  <li>• Custom field selection</li>
                  <li>• Scheduled reports</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-6 rounded-xl">
                <FiUsers className="h-8 w-8 text-teal-600 mb-4" />
                <h3 className="text-lg font-semibold text-primary-900 mb-3">User Management</h3>
                <p className="text-primary-600 mb-3">Complete user lifecycle management with role assignments and permissions.</p>
                <ul className="text-sm text-primary-600 space-y-1">
                  <li>• Role-based user creation</li>
                  <li>• Permission management</li>
                  <li>• User activation/deactivation</li>
                  <li>• Password policies</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-xl">
                <FiDatabase className="h-8 w-8 text-red-600 mb-4" />
                <h3 className="text-lg font-semibold text-primary-900 mb-3">Advanced Search</h3>
                <p className="text-primary-600 mb-3">Powerful search and filtering capabilities across all employee data.</p>
                <ul className="text-sm text-primary-600 space-y-1">
                  <li>• Multi-field search</li>
                  <li>• Status-based filtering</li>
                  <li>• Date range queries</li>
                  <li>• Pagination support</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Security & Compliance */}
          <section id="security" className="bg-white rounded-xl shadow-soft border border-primary-200/50 p-8">
            <div className="flex items-center mb-6">
              <FiShield className="mr-4 h-6 w-6 text-brand-600" />
              <h2 className="text-2xl font-bold text-primary-900">Security & Compliance</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-primary-900 mb-4">Data Protection</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <FiCheckCircle className="h-5 w-5 text-success-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-primary-800">Encryption at Rest</h4>
                      <p className="text-primary-600">All data stored in DynamoDB and S3 is encrypted using AWS KMS.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <FiCheckCircle className="h-5 w-5 text-success-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-primary-800">Encryption in Transit</h4>
                      <p className="text-primary-600">All communication uses HTTPS/TLS encryption.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <FiCheckCircle className="h-5 w-5 text-success-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-primary-800">Secure File Uploads</h4>
                      <p className="text-primary-600">Presigned URLs ensure secure, time-limited file uploads to S3.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-primary-900 mb-4">Access Control</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <FiCheckCircle className="h-5 w-5 text-success-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-primary-800">JWT Authentication</h4>
                      <p className="text-primary-600">Secure token-based authentication with automatic refresh.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <FiCheckCircle className="h-5 w-5 text-success-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-primary-800">Role-Based Access</h4>
                      <p className="text-primary-600">Granular permissions based on user roles and responsibilities.</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <FiCheckCircle className="h-5 w-5 text-success-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-primary-800">Session Management</h4>
                      <p className="text-primary-600">Automatic logout and session timeout for security.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-primary-50 p-6 rounded-xl">
              <h3 className="text-lg font-semibold text-primary-900 mb-4">Audit & Compliance</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FiFileText className="h-8 w-8 text-primary-600" />
                  </div>
                  <h4 className="font-semibold text-primary-900">Complete Audit Trail</h4>
                  <p className="text-primary-600 text-sm">Every action logged with timestamp and user details</p>
                </div>
                <div className="text-center">
                  <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FiEye className="h-8 w-8 text-primary-600" />
                  </div>
                  <h4 className="font-semibold text-primary-900">Field-Level Tracking</h4>
                  <p className="text-primary-600 text-sm">Track changes to individual fields and values</p>
                </div>
                <div className="text-center">
                  <div className="bg-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                    <FiDownload className="h-8 w-8 text-primary-600" />
                  </div>
                  <h4 className="font-semibold text-primary-900">Compliance Reports</h4>
                  <p className="text-primary-600 text-sm">Generate reports for regulatory compliance</p>
                </div>
              </div>
            </div>
          </section>

          {/* Troubleshooting */}
          <section id="troubleshooting" className="bg-white rounded-xl shadow-soft border border-primary-200/50 p-8">
            <div className="flex items-center mb-6">
              <FiInfo className="mr-4 h-6 w-6 text-brand-600" />
              <h2 className="text-2xl font-bold text-primary-900">Troubleshooting</h2>
            </div>

            <div className="space-y-6">
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-lg">
                <h3 className="text-lg font-semibold text-primary-900 mb-3">Common Issues</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-primary-800">File Upload Failures</h4>
                    <p className="text-primary-600 mb-2">If file uploads are failing:</p>
                    <ul className="text-primary-600 space-y-1 ml-4">
                      <li>• Check file size (max 10MB per file)</li>
                      <li>• Ensure file format is supported (PDF, DOC, JPG, PNG)</li>
                      <li>• Try refreshing the page and uploading again</li>
                      <li>• Check your internet connection</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-primary-800">Login Issues</h4>
                    <p className="text-primary-600 mb-2">If you can't log in:</p>
                    <ul className="text-primary-600 space-y-1 ml-4">
                      <li>• Verify your email address is correct</li>
                      <li>• Check if Caps Lock is on</li>
                      <li>• Use the password reset feature if needed</li>
                      <li>• Clear browser cache and cookies</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-primary-800">Application Not Saving</h4>
                    <p className="text-primary-600 mb-2">If your application progress isn't saving:</p>
                    <ul className="text-primary-600 space-y-1 ml-4">
                      <li>• Ensure all required fields are filled</li>
                      <li>• Check your internet connection</li>
                      <li>• Try using a different browser</li>
                      <li>• Disable browser extensions temporarily</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-r-lg">
                <h3 className="text-lg font-semibold text-primary-900 mb-3">Getting Help</h3>
                <p className="text-primary-600 mb-4">
                  If you continue to experience issues, please contact your system administrator or HR department with:
                </p>
                <ul className="text-primary-600 space-y-1">
                  <li>• Description of the problem</li>
                  <li>• Steps you took before the issue occurred</li>
                  <li>• Your browser type and version</li>
                  <li>• Any error messages you received</li>
                  <li>• Screenshots if applicable</li>
                </ul>
              </div>

              <div className="bg-success-50 border-l-4 border-success-400 p-6 rounded-r-lg">
                <h3 className="text-lg font-semibold text-primary-900 mb-3">Best Practices</h3>
                <ul className="text-primary-600 space-y-2">
                  <li>• <strong>Save frequently:</strong> Use the save draft feature regularly</li>
                  <li>• <strong>Use supported browsers:</strong> Chrome, Firefox, Safari, or Edge</li>
                  <li>• <strong>Keep documents ready:</strong> Have all required documents in digital format</li>
                  <li>• <strong>Check email regularly:</strong> Monitor for status updates and feedback</li>
                  <li>• <strong>Follow naming conventions:</strong> Use clear, descriptive names for uploaded files</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage;
